import { authChannel } from "@/infra/auth/broadcast";
import UpdateIcon from "@/shared/icons/Update";
import { setUpdateAvailable } from "@/shared/states/notification";
import { cn } from "@/shared/utils/classname-merger";
import { Card, CardBody, CardHeader } from "@nextui-org/react";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getAgentTypeFromId, useAgentData, useAgentMainData } from "../../states";
import { AgentSourceFile } from "../../types";
import ReassessModal from "../review-responses/ReassessModal";

export default function ReassessUpdateCard({closeFn}: {closeFn: () => void}) {
  const { id } = useParams<{ id: string }>();
  const [openReassess, setOpenReassess] = useState<boolean>(false);
  const [uniqueFiles, setUniqueFiles] = useState<AgentSourceFile[]>([]);
  
  // Get agent data for the modal
  const agentData = useAgentData(id || "");
  const agentMainData = useAgentMainData(id || "");
  
  // Define your styles object
  const styles = {
    headerText: "text-[#52525B]",
    background: "bg-[#F4F4F5] ",
    bodyText: "text-[#27272A]"
  };

  const getPreviousFiles = () => {
    const fileNames: string[] = [];
    const uniqueSources: AgentSourceFile[] = [];
    for (const sourceFileMap of agentMainData?.sourceFilesUrls || []) {
      for (const item of sourceFileMap) {
        const file: AgentSourceFile = item as AgentSourceFile;
        if (file.url) {
          if (!fileNames.includes(file.fileName || "")) {
            fileNames.push(file.fileName || "");
            uniqueSources.push(file);
          }
        }
      }
    }
    setUniqueFiles(uniqueSources);
  };

  const handleReassess = () => {
    getPreviousFiles();
    setOpenReassess(true);
  };

  const onClose = () => {
    setOpenReassess(false);
  };

  useEffect(() => {
    setUpdateAvailable(true);
    return () => {
      setUpdateAvailable(false);
    }
  }, []);

  return (
    <>
      <div className="fixed bottom-5 right-5 z-50">
        <Card
          className={cn(
            "w-[471px]  rounded-lg outline-none border-[1px] relative pb-[5px]",
            styles.background,
          )}
        >
          <X
            onClick={() => closeFn()}
            size={14}
            className="absolute top-[15px] right-[15px] z-20 hover:cursor-pointer"
          />
          <CardHeader
            className={cn(styles.headerText, "pb-0 flex justify-between")}
          >
            <div className="flex flex-row gap-x-[8px]">
              <UpdateIcon color="#52525B" />
              <p className={cn(styles.headerText, "font-medium text-[16px]")}>
                Assessment Update Available
              </p>
            </div>
          </CardHeader>
          <CardBody className={cn(styles.bodyText)}>
            <p className={cn(styles.bodyText, "text-[14px]")}>
              A new version of third-party risk assessment is now available. { }
              <span 
                className="text-[#006FEE] underline cursor-pointer hover:text-[#0054CC]" 
                onClick={handleReassess}
              >
                Reassess
              </span> to view enhancements.
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Reassess Modal */}
      <div className="modal-container">
        <ReassessModal
          agentData={agentData}
          isOpen={openReassess}
          onClose={onClose}
          uniqueSources={uniqueFiles}
        />
      </div>
    </>
  );
}